import { makeObservable, observable, action, computed } from "mobx";

// Models
import { FlowModel, IFlowModel } from "models/flow/FlowModel";
import { FlowPageModel } from "models/flow/FlowPageModel"; // Supondo a existência deste modelo

// Services
import FlowAPI from "services/FlowAPI";
import LocalDBAPI from "services/LocalDBAPI";

/**
 * Store para gerenciar o estado de um fluxo de navegação específico.
 */
class FlowStore {
    // --- Estado Observável ---
    flow?: FlowModel;
    index: number = 0;
    loading: boolean = false;

    constructor() {
        makeObservable(this, {
            // Estado
            flow: observable,
            index: observable,
            loading: observable,

            // Ações
            reset: action,
            emptyFlow: action,
            decrementPage: action,
            incrementPage: action,
            setPageIndex: action,
            get: action,
            logEndSession: action,
            getActualPage: action,
            getPreviousPage: action,
            getNextPage: action,
        });
    }

    /** Reseta o estado da store para os valores iniciais. */
    reset(): void {
        this.flow = undefined;
        this.index = 0;
        this.loading = false;
    }

    /** Cria uma nova instância de um fluxo vazio. */
    emptyFlow(): void {
        this.flow = new FlowModel({ name: '', flowpages: [] });
    }

    // --- Ações de Navegação ---

    /** Decrementa o índice da página atual. */
    decrementPage(): void {
        if (this.index > 0) {
            this.index--;
        }
    }

    /** Incrementa o índice da página atual. */
    incrementPage(): void {
        if (this.flow && this.index + 1 < this.flow.flowpages.length) {
            this.index++;
        }
    }

    /** Define o índice da página para um valor específico. */
    setPageIndex(index: number): void {
        if (this.flow && index >= 0 && index < this.flow.flowpages.length) {
            this.index = index;
        }
    }

    // --- Getters Computados ---

    /** Retorna o objeto da página atual no fluxo. */
    getActualPage(): FlowPageModel | undefined {
        return this.flow?.flowpages[this.index];
    }

    /** Retorna o objeto da página anterior no fluxo, se houver. */
    getPreviousPage(): FlowPageModel | undefined {
        if (this.index > 0) {
            return this.flow?.flowpages[this.index - 1];
        }
        return undefined;
    }

    /** Retorna o objeto da próxima página no fluxo, se houver. */
    getNextPage(): FlowPageModel | undefined {
        if (this.flow && this.index + 1 < this.flow.flowpages.length) {
            return this.flow.flowpages[this.index + 1];
        }
        return undefined;
    }

    // --- Ações Assíncronas ---

    /** Busca os dados de um fluxo pelo UUID. */
    async get(uuid: string): Promise<FlowModel | undefined> {
        this.loading = true;
        try {
            const response = await FlowAPI.get(uuid);
            if (!response.error) {
                this.flow = new FlowModel(response);
                return this.flow;
            } else {
                console.error("Erro ao buscar fluxo:", response.error);
            }
        } catch (error) {
            console.error("Falha na chamada API para buscar fluxo:", error);
        } finally {
            this.loading = false;
        }
        return undefined;
    }


    /** Realiza avaliação do fluxo */
    async evaluateAllocation(data: EvaluateAllocationPayloadModel): Promise<boolean> {
        this.loading = true;
        try {
            const response = await FlowAPI.evaluateAllocation(data);
            if (!response.error) {
                return true;
            } else {
                console.error("Erro ao avaliar experiência:", response.error);
            }
        } catch (error) {
            console.error("Falha na chamada API avaliar experiência:", error);
        } finally {
            this.loading = false;
        }
        return false;
    }

    /** Registra o fim de uma sessão no banco de dados local. */
    async logEndSession(): Promise<void> {
        try {
            await LocalDBAPI.endSession();
        } catch (error) {
            console.error("Falha ao registrar log de fim de sessão:", error);
        }
    }
}

export default FlowStore;